import { useState, useEffect } from 'react';

interface RequestFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function RequestFormModal({ isOpen, onClose }: RequestFormModalProps) {
  const [status, setStatus] = useState<SubmitStatus>('idle');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setTimeout(() => setStatus('idle'), 300);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) {
      return;
    }
    setStatus('submitting');
    try {
      const data = new FormData(form);
      const params = new URLSearchParams();
      data.forEach((value, key) => {
        params.append(key, value.toString());
      });
      const res = await fetch('https://readdy.ai/api/form/d70j5n9qa83q0hmd1aeg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-3xl overflow-hidden">
        {/* Header */}
        <div className="bg-[#8FA882] px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/20">
                <i className="ri-file-list-3-line text-white text-lg" />
              </div>
              <div>
                <h2 className="text-white font-bold text-lg leading-tight">無料 資料請求</h2>
                <p className="text-white/80 text-xs mt-0.5">鳥取エリアの家づくり情報をお届けします</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors cursor-pointer"
              aria-label="閉じる"
            >
              <i className="ri-close-line text-white" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-8 py-7 max-h-[70vh] overflow-y-auto">
          {status === 'success' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#E8F0E6] mx-auto mb-4">
                <i className="ri-check-line text-[#8FA882] text-3xl" />
              </div>
              <h3 className="text-[#2C2C2C] font-bold text-lg mb-2">送信完了しました！</h3>
              <p className="text-[#6B6B6B] text-sm leading-relaxed">
                資料請求を受け付けました。<br />
                ご登録のメールアドレスに詳細をお送りします。
              </p>
              <button
                onClick={onClose}
                className="mt-6 px-8 py-3 rounded-full bg-[#8FA882] text-white font-semibold text-sm hover:bg-[#7A9570] transition-colors cursor-pointer whitespace-nowrap"
              >
                閉じる
              </button>
            </div>
          ) : status === 'error' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-50 mx-auto mb-4">
                <i className="ri-error-warning-line text-red-400 text-3xl" />
              </div>
              <h3 className="text-[#2C2C2C] font-bold text-lg mb-2">送信に失敗しました</h3>
              <p className="text-[#6B6B6B] text-sm">お手数ですが、もう一度お試しください。</p>
              <button
                onClick={() => setStatus('idle')}
                className="mt-6 px-8 py-3 rounded-full bg-[#8FA882] text-white font-semibold text-sm hover:bg-[#7A9570] transition-colors cursor-pointer whitespace-nowrap"
              >
                戻る
              </button>
            </div>
          ) : (
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              {/* Name */}
              <div>
                <label className="block text-[#2C2C2C] text-sm font-semibold mb-1.5">
                  お名前 <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="例：山田 太郎"
                  className="w-full px-4 py-3 rounded-xl border border-[#E0DAD2] text-sm text-[#2C2C2C] placeholder-[#C0B8B0] focus:outline-none focus:border-[#8FA882] focus:ring-2 focus:ring-[#8FA882]/20 transition-all"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-[#2C2C2C] text-sm font-semibold mb-1.5">
                  メールアドレス <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="例：taro@example.com"
                  className="w-full px-4 py-3 rounded-xl border border-[#E0DAD2] text-sm text-[#2C2C2C] placeholder-[#C0B8B0] focus:outline-none focus:border-[#8FA882] focus:ring-2 focus:ring-[#8FA882]/20 transition-all"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-[#2C2C2C] text-sm font-semibold mb-1.5">
                  電話番号
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="例：0857-00-0000"
                  className="w-full px-4 py-3 rounded-xl border border-[#E0DAD2] text-sm text-[#2C2C2C] placeholder-[#C0B8B0] focus:outline-none focus:border-[#8FA882] focus:ring-2 focus:ring-[#8FA882]/20 transition-all"
                />
              </div>

              {/* Area */}
              <div>
                <label className="block text-[#2C2C2C] text-sm font-semibold mb-1.5">
                  希望エリア
                </label>
                <select
                  name="area"
                  className="w-full px-4 py-3 rounded-xl border border-[#E0DAD2] text-sm text-[#2C2C2C] focus:outline-none focus:border-[#8FA882] focus:ring-2 focus:ring-[#8FA882]/20 transition-all bg-white cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="鳥取市">鳥取市</option>
                  <option value="米子市">米子市</option>
                  <option value="倉吉市">倉吉市</option>
                  <option value="境港市">境港市</option>
                  <option value="その他">その他</option>
                </select>
              </div>

              {/* Property Type */}
              <div>
                <label className="block text-[#2C2C2C] text-sm font-semibold mb-2">
                  希望する住宅の種類
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {['新築戸建て', '中古戸建て', 'マンション'].map((type) => (
                    <label
                      key={type}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-[#E0DAD2] cursor-pointer hover:border-[#8FA882] transition-colors has-[:checked]:border-[#8FA882] has-[:checked]:bg-[#E8F0E6]"
                    >
                      <input type="radio" name="propertyType" value={type} className="accent-[#8FA882]" />
                      <span className="text-xs text-[#2C2C2C]">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Budget */}
              <div>
                <label className="block text-[#2C2C2C] text-sm font-semibold mb-1.5">
                  おおよその予算
                </label>
                <select
                  name="budget"
                  className="w-full px-4 py-3 rounded-xl border border-[#E0DAD2] text-sm text-[#2C2C2C] focus:outline-none focus:border-[#8FA882] focus:ring-2 focus:ring-[#8FA882]/20 transition-all bg-white cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="2000万円未満">2,000万円未満</option>
                  <option value="2000〜3000万円">2,000〜3,000万円</option>
                  <option value="3000〜4000万円">3,000〜4,000万円</option>
                  <option value="4000万円以上">4,000万円以上</option>
                  <option value="未定">まだ未定</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <label className="block text-[#2C2C2C] text-sm font-semibold mb-1.5">
                  ご質問・ご要望（任意）
                </label>
                <textarea
                  name="message"
                  maxLength={500}
                  rows={3}
                  placeholder="気になることや知りたいことを自由にご記入ください（500文字以内）"
                  className="w-full px-4 py-3 rounded-xl border border-[#E0DAD2] text-sm text-[#2C2C2C] placeholder-[#C0B8B0] focus:outline-none focus:border-[#8FA882] focus:ring-2 focus:ring-[#8FA882]/20 transition-all resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full py-4 rounded-full bg-[#8FA882] text-white font-bold text-base hover:bg-[#7A9570] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {status === 'submitting' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    送信中...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-send-plane-line" />
                    無料で資料請求する
                  </span>
                )}
              </button>

              <p className="text-center text-[#A0A0A0] text-[10px] leading-relaxed">
                送信することで、プライバシーポリシーに同意したものとみなします。
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
